import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, TextField, Switch, FormControlLabel, Chip,
    Autocomplete, IconButton, Stack, Grid, MenuItem, Select,
    Snackbar, Alert, Divider, InputAdornment, Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { APIURL } from '../../../config';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_PREFERENCES = {
    driving_distance_km: '',
    preferred_hours: [],
    skills: [],
    overtime_acceptable: false,
    additional_preferences: [],
};

const CaregiverPreferences = ({ employee, com_id, user, type, leftColumnSize = 4, rightColumnSize = 8 }) => {
    const isAdmin = (user?.type || type) === 'Admin';

    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ ...DEFAULT_PREFERENCES });

    // Company master lists (fetched from API)
    const [skillsMasterList, setSkillsMasterList] = useState([]);
    const [preferencesMasterList, setPreferencesMasterList] = useState([]);
    const [newSkillInput, setNewSkillInput] = useState('');
    const [newPrefInput, setNewPrefInput] = useState('');

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState('success');
    const [alertMessage, setAlertMessage] = useState('');

    // Auto-save refs
    const saveTimeout = useRef(null);
    const isInitialLoad = useRef(true);

    useEffect(() => {
        if (employee?.emp_id) {
            fetchPreferences();
        }
    }, [employee?.emp_id]);

    // Fetch master lists on mount
    useEffect(() => {
        if (com_id) {
            fetchMasterSkills();
            fetchMasterPreferences();
        }
    }, [com_id]);

    // Auto-save when formData changes (skip initial load)
    useEffect(() => {
        if (isInitialLoad.current) return;
        if (!employee?.emp_id) return;

        if (saveTimeout.current) clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(() => {
            autoSave();
        }, 800);

        return () => {
            if (saveTimeout.current) clearTimeout(saveTimeout.current);
        };
    }, [formData]);

    const fetchPreferences = async () => {
        try {
            setLoading(true);
            isInitialLoad.current = true;
            const response = await axios.get(APIURL + '/caregiverPreferences', {
                params: { employee_id: employee.emp_id },
            });
            if (response.data) {
                setFormData({
                    driving_distance_km: response.data.driving_distance_km ?? '',
                    preferred_hours: response.data.preferred_hours || [],
                    skills: response.data.skills || [],
                    overtime_acceptable: response.data.overtime_acceptable === true || response.data.overtime_acceptable === 1,
                    additional_preferences: response.data.additional_preferences || [],
                });
            } else {
                setFormData({ ...DEFAULT_PREFERENCES });
            }
            // Allow auto-save after initial data is set
            setTimeout(() => { isInitialLoad.current = false; }, 100);
        } catch (error) {
            console.error('Error fetching caregiver preferences:', error);
            isInitialLoad.current = false;
        } finally {
            setLoading(false);
        }
    };

    const fetchMasterSkills = async () => {
        try {
            const response = await axios.get(APIURL + '/caregiverPreferences/masterSkills', {
                params: { com_id },
            });
            setSkillsMasterList(response.data.map(item => ({ id: item.id, name: item.skill_name })));
        } catch (error) {
            console.error('Error fetching master skills:', error);
        }
    };

    const fetchMasterPreferences = async () => {
        try {
            const response = await axios.get(APIURL + '/caregiverPreferences/masterPreferences', {
                params: { com_id },
            });
            setPreferencesMasterList(response.data.map(item => ({ id: item.id, name: item.preference_name })));
        } catch (error) {
            console.error('Error fetching master preferences:', error);
        }
    };

    const autoSave = async () => {
        try {
            await axios.post(APIURL + '/caregiverPreferences', {
                employee_id: employee.emp_id,
                driving_distance_km: formData.driving_distance_km !== '' ? parseFloat(formData.driving_distance_km) : null,
                preferred_hours: formData.preferred_hours,
                skills: formData.skills,
                overtime_acceptable: formData.overtime_acceptable,
                additional_preferences: formData.additional_preferences,
            });
        } catch (error) {
            console.error('Error saving caregiver preferences:', error);
            showAlert('error', 'Failed to save caregiver preferences');
        }
    };

    const showAlert = (severity, message) => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    };

    // Preferred Hours helpers
    const addHourSlot = () => {
        setFormData({ ...formData, preferred_hours: [...formData.preferred_hours, { day: 'Monday', startTime: '08:00', endTime: '17:00' }] });
    };

    const updateHourSlot = (index, field, value) => {
        const updated = [...formData.preferred_hours];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({ ...formData, preferred_hours: updated });
    };

    const removeHourSlot = (index) => {
        setFormData({ ...formData, preferred_hours: formData.preferred_hours.filter((_, i) => i !== index) });
    };

    // Skills master list helpers (admin only)
    const addToSkillsMaster = async () => {
        const trimmed = newSkillInput.trim();
        if (!trimmed) return;
        try {
            await axios.post(APIURL + '/caregiverPreferences/masterSkills', { com_id, skill_name: trimmed });
            setNewSkillInput('');
            fetchMasterSkills();
        } catch (error) {
            if (error.response?.status === 409) {
                showAlert('warning', 'Skill already exists');
            } else {
                showAlert('error', 'Failed to add skill');
            }
        }
    };

    const removeFromSkillsMaster = async (item) => {
        try {
            await axios.delete(APIURL + '/caregiverPreferences/masterSkills/' + item.id);
            fetchMasterSkills();
            // Also remove from employee's selected skills
            if (formData.skills.includes(item.name)) {
                setFormData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== item.name) }));
            }
        } catch (error) {
            showAlert('error', 'Failed to remove skill');
        }
    };

    // Preferences master list helpers (admin only)
    const addToPrefsMaster = async () => {
        const trimmed = newPrefInput.trim();
        if (!trimmed) return;
        try {
            await axios.post(APIURL + '/caregiverPreferences/masterPreferences', { com_id, preference_name: trimmed });
            setNewPrefInput('');
            fetchMasterPreferences();
        } catch (error) {
            if (error.response?.status === 409) {
                showAlert('warning', 'Preference already exists');
            } else {
                showAlert('error', 'Failed to add preference');
            }
        }
    };

    const removeFromPrefsMaster = async (item) => {
        try {
            await axios.delete(APIURL + '/caregiverPreferences/masterPreferences/' + item.id);
            fetchMasterPreferences();
            // Also remove from employee's selected preferences
            if (formData.additional_preferences.includes(item.name)) {
                setFormData(prev => ({ ...prev, additional_preferences: prev.additional_preferences.filter(p => p !== item.name) }));
            }
        } catch (error) {
            showAlert('error', 'Failed to remove preference');
        }
    };

    const FieldRow = ({ label, children }) => (
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
            <Grid size={4}>
                <Typography variant="body1" sx={{ pt: 0.5 }}>{label}</Typography>
            </Grid>
            <Grid size={8}>
                {children}
            </Grid>
        </Grid>
    );

    const MasterListManager = ({ list, onAdd, onRemove, inputValue, onInputChange, placeholder }) => (
        <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Available Options</Typography>
            <Box sx={{ mb: 1.5, minHeight: 28 }}>
                {list.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No options added yet</Typography>
                )}
                {list.map(item => (
                    <Chip key={item.id} label={item.name} color="primary" size="small" onDelete={() => onRemove(item)} sx={{ mr: 1, mb: 1 }} />
                ))}
            </Box>
            <Stack direction="row" spacing={1} sx={{ maxWidth: 360 }}>
                <TextField
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    size="small"
                    placeholder={placeholder}
                    onKeyDown={(e) => e.key === 'Enter' && onAdd()}
                    sx={{ flexGrow: 1 }}
                />
                <Button variant="outlined" size="small" onClick={onAdd} disabled={!inputValue.trim()} startIcon={<AddIcon />}>
                    Add
                </Button>
            </Stack>
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                {/* Left column: title + description */}
                <Grid size={leftColumnSize}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            Caregiver Matchmaking Preferences
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Configure preferences used for matching caregivers to shifts
                        </Typography>
                    </Stack>
                </Grid>

                {/* Right column: fields */}
                <Grid size={rightColumnSize}>
                    {/* Driving Distance */}
                    <FieldRow label="Acceptable Driving Distance">
                        <TextField
                            type="number"
                            value={formData.driving_distance_km}
                            onChange={(e) => setFormData({ ...formData, driving_distance_km: e.target.value })}
                            size="small"
                            sx={{ width: 180 }}
                            inputProps={{ min: 0, step: 1 }}
                            slotProps={{ input: { endAdornment: <InputAdornment position="end">Miles</InputAdornment> } }}
                        />
                    </FieldRow>

                    {/* Preferred Weekly Hours */}
                    <FieldRow label="Preferred Weekly Hours">
                        <Box>
                            {formData.preferred_hours.map((slot, index) => (
                                <Grid container spacing={1} key={index} sx={{ mb: 1, alignItems: 'center' }}>
                                    <Grid size={3.5}>
                                        <Select value={slot.day} onChange={(e) => updateHourSlot(index, 'day', e.target.value)} size="small" fullWidth>
                                            {DAYS_OF_WEEK.map(day => <MenuItem key={day} value={day}>{day}</MenuItem>)}
                                        </Select>
                                    </Grid>
                                    <Grid size={2.5}>
                                        <TextField type="time" value={slot.startTime} onChange={(e) => updateHourSlot(index, 'startTime', e.target.value)} size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                                    </Grid>
                                    <Grid size={0.5}>
                                        <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>—</Typography>
                                    </Grid>
                                    <Grid size={2.5}>
                                        <TextField type="time" value={slot.endTime} onChange={(e) => updateHourSlot(index, 'endTime', e.target.value)} size="small" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
                                    </Grid>
                                    <Grid size={1.5}>
                                        <IconButton size="small" onClick={() => removeHourSlot(index)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={addHourSlot} sx={{ mt: formData.preferred_hours.length > 0 ? 0.5 : 0 }}>
                                Add Day
                            </Button>
                        </Box>
                    </FieldRow>

                    {/* Overtime */}
                    <FieldRow label="Overtime">
                        <FormControlLabel
                            control={<Switch checked={formData.overtime_acceptable} onChange={(e) => setFormData({ ...formData, overtime_acceptable: e.target.checked })} />}
                            label="Acceptable"
                        />
                    </FieldRow>

                    <Divider sx={{ my: 2 }} />

                    {/* Skills */}
                    <FieldRow label="Skills">
                        <Autocomplete
                            multiple
                            options={skillsMasterList.map(s => s.name).filter(s => !formData.skills.includes(s))}
                            value={formData.skills}
                            onChange={(e, newValue) => setFormData({ ...formData, skills: newValue })}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option} size="small" color="primary" {...getTagProps({ index })} key={option} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Select skills" />
                            )}
                        />
                    </FieldRow>
                    {isAdmin && (
                        <FieldRow label="Skill Options">
                            <MasterListManager
                                list={skillsMasterList}
                                onAdd={addToSkillsMaster}
                                onRemove={removeFromSkillsMaster}
                                inputValue={newSkillInput}
                                onInputChange={setNewSkillInput}
                                placeholder="New skill"
                            />
                        </FieldRow>
                    )}

                    {/* Additional Preferences */}
                    <FieldRow label="Additional Preferences">
                        <Autocomplete
                            multiple
                            options={preferencesMasterList.map(p => p.name).filter(p => !formData.additional_preferences.includes(p))}
                            value={formData.additional_preferences}
                            onChange={(e, newValue) => setFormData({ ...formData, additional_preferences: newValue })}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option} size="small" color="secondary" {...getTagProps({ index })} key={option} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Select preferences" />
                            )}
                        />
                    </FieldRow>
                    {isAdmin && (
                        <FieldRow label="Preference Options">
                            <MasterListManager
                                list={preferencesMasterList}
                                onAdd={addToPrefsMaster}
                                onRemove={removeFromPrefsMaster}
                                inputValue={newPrefInput}
                                onInputChange={setNewPrefInput}
                                placeholder="New preference"
                            />
                        </FieldRow>
                    )}
                </Grid>
            </Grid>

            <Snackbar
                open={alertOpen}
                autoHideDuration={5000}
                onClose={() => setAlertOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity} variant="filled" sx={{ width: '100%' }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CaregiverPreferences;
